// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Declare custom deck presets for common roof shapes.
 * @author James Reid
 */

// @ts-check

// @@imports-module
import { Deck } from "../Deck.js"

// @@imports-utils
import { Vector2D } from "../utils/index.js"

// @@imports-types
import { DeckOption } from "../types/DeckOption.js"

// @@body
const presets = /** @type {Object.<string,DeckOption>} */ ({
    rectangular: {
        deck: new Deck({
            vertices: [
                new Vector2D(0, 0),
                new Vector2D(500, 0),
                new Vector2D(500, 500),
                new Vector2D(0, 500)
            ],
            isClosed: true
        }),
        imageUrl: null,
        id: "square",
        title: "Square/Rectangular",
        description: "Standard square or rectangular roof."
    },
    rightL: {
        deck: new Deck({
            vertices: [
                new Vector2D(0, 0),
                new Vector2D(300, 0),
                new Vector2D(300, 300),
                new Vector2D(600, 300),
                new Vector2D(600, 600),
                new Vector2D(0, 600)
            ],
            isClosed: true
        }),
        imageUrl: null,
        id: "l-shaped",
        title: "L-Shaped",
        description: "Right \"L\" shaped roof."
    },
    standardT: {
        deck: new Deck({
            vertices: [
                new Vector2D(0, 0),
                new Vector2D(750, 0),
                new Vector2D(750, 250),
                new Vector2D(500, 250),
                new Vector2D(500, 500),
                new Vector2D(250, 500),
                new Vector2D(250, 250),
                new Vector2D(0, 250)
            ],
            isClosed: true
        }),
        imageUrl: null,
        id: "t-shaped",
        title: "T-Shaped",
        description: "Downward \"T\" shaped roof."
    },
    standardS: {
        deck: new Deck({
            vertices: [
                new Vector2D(250, 0),
                new Vector2D(750, 0),
                new Vector2D(750, 250),
                new Vector2D(500, 250),
                new Vector2D(500, 500),
                new Vector2D(0, 500),
                new Vector2D(0, 250),
                new Vector2D(250, 250)
            ],
            isClosed: true
        }),
        imageUrl: null,
        id: "s-shaped",
        title: "S-Shaped",
        description: "Slanted \"S\" shaped roof."
    },
    standardU: {
        deck: new Deck({
            vertices: [
                new Vector2D(0, 0),
                new Vector2D(225, 0),
                new Vector2D(225, 225),
                new Vector2D(450, 225),
                new Vector2D(450, 0),
                new Vector2D(675, 0),
                new Vector2D(675, 450),
                new Vector2D(0, 450)
            ],
            isClosed: true
        }),
        imageUrl: null,
        id: "u-shaped",
        title: "U-Shaped",
        description: "Upward \"U\" shaped roof."
    }
})

// @@exports
export { presets }
